"use client"

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { format } from "date-fns"
import { MedicalRecord, Prescription, Admission } from "@/types"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"

type MedicalRecordUnion = (MedicalRecord & { type: 'record' }) | 
                          (Prescription & { type: 'prescription' }) | 
                          (Admission & { type: 'admission', created_at: string })

interface Vital {
    id: string;
    created_at: string;
    blood_pressure: string | null;
    heart_rate: number | null;
    temperature: number | null;
    weight_kg: number | null;
    height_cm: number | null;
    notes: string | null;
}

interface RecordsHistoryTabProps {
  records: MedicalRecordUnion[];
  vitals: Vital[];
}

// Combine and sort records and vitals by date
const combineAndSort = (records: MedicalRecordUnion[], vitals: any[]) => {
    const combined = [
        ...records.map(r => {
            if (r.type === 'admission') {
                return { ...r, created_at: r.admission_date };
            }
            return r;
        }),
        ...vitals.map(v => ({ ...v, type: 'vitals' }))
    ];
    return combined.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

const renderRecordItem = (item: MedicalRecordUnion) => {
    switch(item.type) {
      case 'record':
        return (
          <Card key={item.id} className="mb-4">
            <CardHeader>
              <CardTitle className="text-lg">Medical Record - {format(new Date(item.created_at), 'PPP')}</CardTitle>
              <CardDescription>
                Recorded by: {item.doctors?.name || "N/A"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <h4 className="font-semibold">Diagnosis</h4>
                  <p className="text-muted-foreground">{item.diagnosis || "No diagnosis provided."}</p>
                </div>
                <div>
                  <h4 className="font-semibold">Visit Notes</h4>
                  <p className="text-muted-foreground whitespace-pre-wrap">{item.visit_notes || "No notes provided."}</p>
                </div>
                {item.blood_pressure && (
                  <div>
                    <h4 className="font-semibold">Vitals</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p>BP: <span className="font-medium">{item.blood_pressure}</span></p>
                      <p>HR: <span className="font-medium">{item.heart_rate} bpm</span></p>
                      <p>Temp: <span className="font-medium">{item.temperature}°C</span></p>
                      <p>Weight: <span className="font-medium">{item.weight_kg}kg</span></p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

export function RecordsHistoryTab({ records, vitals }: RecordsHistoryTabProps) {
  const allRecords = combineAndSort(records, vitals);

  if (allRecords.length === 0) {
    return <p className="text-center text-muted-foreground mt-4">No medical records or vitals found.</p>
  }
  
  return (
    <Accordion type="single" collapsible className="w-full">
        {allRecords.map((item, index) => (
            <AccordionItem value={`item-${index}`} key={item.id}>
                <AccordionTrigger>
                    <div className="flex justify-between w-full pr-4">
                        <span className="font-semibold">
                            {item.type === 'record' ? `Consultation Note` : 'Vitals Recorded'}
                        </span>
                        <span className="text-muted-foreground text-sm">
                            {format(new Date(item.created_at), "PPP")}
                        </span>
                    </div>
                </AccordionTrigger>
                <AccordionContent>
                    {renderRecordItem(item)}
                </AccordionContent>
            </AccordionItem>
        ))}
    </Accordion>
  )
} 